"use client";

import { c as _c } from "react/compiler-runtime";
import type { OrganizationMembershipSummary } from "@clutch/auth/actions/users";
import { useQuery } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { getOrganizationMembershipsAction } from "@/app/_actions/organizations";
import { DashboardSwitchOverlay } from "@/components/blocks/DashboardSwitchOverlay";
import Footer from "@/components/blocks/Footer";
import Header from "@/components/blocks/Header";
import { InstallAppBanner } from "@/components/blocks/InstallAppBanner";
import MobileBottomNav from "@/components/blocks/MobileBottomNav";
import { NotifyPermissionBanner } from "@/components/blocks/NotifyPermissionBanner";
import { SidebarLayout } from "@/components/layouts/SidebarLayout";
import { isChromelessRoute } from "@/config/layout-config";
import { authClient } from "@/lib/auth-client";
import { bumpEngagement as bumpInstallEngagement } from "@/lib/pwa-install-storage";
import { bumpEngagement as bumpNotifEngagement } from "@/lib/notification-prompt-storage";
import { cn } from "@/lib/utils";
const PUBLIC_ONLY_ROUTES = ["/", "/contact", "/terms", "/privacy", "/cookie-policy"];
const PUBLIC_ONLY_PREFIXES = ["/p/"];
function isPublicOnlyRoute(pathname: string | null) {
  if (!pathname) return false;
  return PUBLIC_ONLY_ROUTES.includes(pathname) || PUBLIC_ONLY_PREFIXES.some(prefix => pathname.startsWith(prefix));
}
export default function AppShell(t0) {
  const $ = _c(44);
  const {
    children,
    initialHasAuthenticatedUser,
    initialMemberships,
    defaultSidebarOpen
  } = t0;
  const pathname = usePathname();
  const {
    data: session
  } = authClient.useSession();
  let t1;
  let t2;
  if ($[0] !== pathname) {
    t1 = () => {
      if (!pathname) {
        return;
      }
      if (isChromelessRoute(pathname)) {
        return;
      }
      bumpInstallEngagement();
      bumpNotifEngagement();
    };
    t2 = [pathname];
    $[0] = pathname;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["organization-memberships"];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = Boolean(session?.user) || initialHasAuthenticatedUser;
  let t5;
  if ($[4] !== initialMemberships || $[5] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: _temp,
      staleTime: 300000,
      enabled: t4,
      placeholderData: initialMemberships
    };
    $[4] = initialMemberships;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    data: t6
  } = useQuery(t5);
  const memberships = t6 === undefined ? initialMemberships : t6;
  const hasMemberships = memberships.length > 0;
  const hasAuthenticatedUser = Boolean(session?.user) || initialHasAuthenticatedUser || hasMemberships;
  if (isChromelessRoute(pathname)) {
    let t7;
    if ($[7] !== children) {
      t7 = <>{children}</>;
      $[7] = children;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    return t7;
  }
  let t7;
  if ($[9] !== hasAuthenticatedUser || $[10] !== pathname) {
    t7 = false || hasAuthenticatedUser && !isPublicOnlyRoute(pathname) && !isChromelessRoute(pathname);
    $[9] = hasAuthenticatedUser;
    $[10] = pathname;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const showInstallBanner = t7;
  if (hasAuthenticatedUser && hasMemberships && !isPublicOnlyRoute(pathname)) {
    let t8;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <DashboardSwitchOverlay />;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] !== showInstallBanner) {
      t9 = showInstallBanner && <InstallAppBanner />;
      $[13] = showInstallBanner;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    let t10;
    if ($[15] !== hasAuthenticatedUser) {
      t10 = hasAuthenticatedUser && <NotifyPermissionBanner />;
      $[15] = hasAuthenticatedUser;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    let t11;
    if ($[17] !== children || $[18] !== defaultSidebarOpen || $[19] !== t10 || $[20] !== t9) {
      t11 = <SidebarLayout defaultOpen={defaultSidebarOpen}>{t8}{children}{t9}{t10}</SidebarLayout>;
      $[17] = children;
      $[18] = defaultSidebarOpen;
      $[19] = t10;
      $[20] = t9;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    return t11;
  }
  const isLanding = pathname === "/";
  let t8;
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DashboardSwitchOverlay />;
    t9 = <Header />;
    $[22] = t8;
    $[23] = t9;
  } else {
    t8 = $[22];
    t9 = $[23];
  }
  const t10 = hasAuthenticatedUser && "pb-[calc(env(safe-area-inset-bottom)+5rem)] md:pb-0";
  let t11;
  if ($[24] !== t10) {
    t11 = cn("flex-1", t10);
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== children || $[27] !== t11) {
    t12 = <main className={t11}>{children}</main>;
    $[26] = children;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <MobileBottomNav />;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = !isLanding && "hidden md:block";
  let t15;
  if ($[30] !== t14) {
    t15 = cn(t14);
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Footer />;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t15) {
    t17 = <div className={t15}>{t16}</div>;
    $[33] = t15;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== showInstallBanner) {
    t18 = showInstallBanner && <InstallAppBanner hasBottomNav={true} />;
    $[35] = showInstallBanner;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== hasAuthenticatedUser) {
    t19 = hasAuthenticatedUser && <NotifyPermissionBanner hasBottomNav={true} />;
    $[37] = hasAuthenticatedUser;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t12 || $[40] !== t17 || $[41] !== t18 || $[42] !== t19) {
    t20 = <div className="flex min-h-svh flex-col">{t8}{t9}{t12}{t13}{t17}{t18}{t19}</div>;
    $[39] = t12;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  return t20;
}
function _temp() {
  return getOrganizationMembershipsAction();
}